import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useRefresh,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { JustifiedAction } from '../actions/JustifiedAction';
import { CrossRegionConfirm } from '../actions/CrossRegionConfirm';
import { performStatusTransition } from '../actions/performStatusTransition';
import type { AccessibleRegion } from '../auth/permissions';
import type {
  AdminDataProvider,
  AlertChannelConfig,
  AlertConfig,
  AlertType,
  Market,
} from '../dataProvider';
import { ALERT_TYPES } from '../dataProvider';
import { ALERT_TYPE_LABEL, isMonetaryAlert } from './alertsModel';

/**
 * The internal-alerts configuration surface (D-internal-alerts, Phase 9), the CONFIG half of
 * the last feature surface. A config page in the S1 / S4 config-section shape, one card per
 * alert type plus the global channel config. super_admin-only (alerts:write): a mis-set
 * threshold means a real alert does not fire or floods, so this is consequential operational
 * platform config.
 *
 *  - The MONETARY types (high_value_cashout, large_refund) are REGION-KEYED: a per-market
 *    threshold row, edited with a FORCED reason (JustifiedAction), and a both-market change
 *    routes through CrossRegionConfirm plus two per-market audited writes (the S1 / S2 pattern).
 *  - suspicious_activity is GLOBAL: a count in a window, forced-reason edit.
 *  - system_error is GLOBAL with no threshold: only the enabled and channel toggles.
 *  - The global CHANNEL config holds the Slack webhook (a SECRET: write-only masked, redacted
 *    in the audit via SENSITIVE_AUDIT_FIELDS, the S2 credential treatment) plus the email
 *    recipients and frequency (plain audited).
 *
 * The admin CONFIGURES here; the backend FIRES and DELIVERS (the migration seam). There is no
 * send-a-test-alert affordance: the admin never posts to Slack or sends a digest.
 */

const nowIso = () => new Date().toISOString();
const marketToRegion = (m: Market): AccessibleRegion =>
  m === 'us' ? 'US' : 'UK';
const usdOrGbp = (m: AlertConfig['market']): string =>
  m === 'us' ? '$' : m === 'uk' ? '£' : '';

/** The enabled + slack + email toggles for one config row (plain audited writes). */
const RowToggles = ({ row, email }: { row: AlertConfig; email: string }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();

  const toggle = async (field: 'enabled' | 'slackEnabled' | 'emailEnabled') => {
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'alerts:write',
        resource: 'alertConfigs',
        id: row.id,
        data: { [field]: !row[field], updatedBy: email, updatedAt: nowIso() },
        previousData: row,
      });
      refresh();
    } catch {
      notify('Could not update the alert', { type: 'error' });
    }
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
    >
      <FormControlLabel
        control={
          <Switch
            checked={row.enabled}
            onChange={() => void toggle('enabled')}
            data-testid={`alert-enabled-${row.id}`}
          />
        }
        label="Enabled"
      />
      <FormControlLabel
        control={
          <Switch
            checked={row.slackEnabled}
            onChange={() => void toggle('slackEnabled')}
            data-testid={`alert-slack-${row.id}`}
          />
        }
        label="Slack"
      />
      <FormControlLabel
        control={
          <Switch
            checked={row.emailEnabled}
            onChange={() => void toggle('emailEnabled')}
            data-testid={`alert-email-${row.id}`}
          />
        }
        label="Email"
      />
    </Stack>
  );
};

/** One monetary market row: the per-market threshold with a forced-reason save. */
const MonetaryRow = ({ row, email }: { row: AlertConfig; email: string }) => {
  const [value, setValue] = useState(String(row.thresholdAmount ?? ''));
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
    >
      <Typography variant="body2" sx={{ minWidth: 48 }}>
        {String(row.market).toUpperCase()}
      </Typography>
      <RowToggles row={row} email={email} />
      <TextField
        label={`Threshold (${usdOrGbp(row.market)})`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        type="number"
        margin="dense"
        size="small"
        sx={{ minWidth: 180 }}
        inputProps={{ 'data-testid': `alert-threshold-${row.id}` }}
      />
      <JustifiedAction
        resource="alertConfigs"
        capability="alerts:write"
        id={row.id}
        label={`Save ${ALERT_TYPE_LABEL[row.alertType]} ${String(row.market).toUpperCase()}`}
        dialogTitle={`Change the ${ALERT_TYPE_LABEL[row.alertType]} ${String(row.market).toUpperCase()} threshold`}
        dialogIntro="This threshold drives when the alert fires. Record why it is changing."
        buildData={(reason) => ({
          thresholdAmount: Number(value),
          lastChangeReason: reason,
          updatedBy: email,
          updatedAt: nowIso(),
        })}
        previousData={row}
        successMessage="Threshold saved"
      />
    </Stack>
  );
};

/** A monetary (region-keyed) alert card: per-market rows plus a both-market apply. */
const MonetaryTypeCard = ({
  type,
  rows,
  email,
}: {
  type: AlertType;
  rows: AlertConfig[];
  email: string;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();
  const [bothValue, setBothValue] = useState('');
  const [bothReason, setBothReason] = useState('');
  const [pendingCross, setPendingCross] = useState(false);
  const [busy, setBusy] = useState(false);

  const sorted = [...rows].sort((a, b) =>
    String(a.market).localeCompare(String(b.market)),
  );

  const saveBoth = async () => {
    setBusy(true);
    try {
      for (const row of sorted) {
        await performStatusTransition({
          dataProvider,
          role,
          capability: 'alerts:write',
          resource: 'alertConfigs',
          id: row.id,
          data: {
            thresholdAmount: Number(bothValue),
            lastChangeReason: bothReason.trim(),
            updatedBy: email,
            updatedAt: nowIso(),
          },
          previousData: row,
        });
      }
      notify('Threshold saved to both markets', { type: 'success' });
      setPendingCross(false);
      setBothValue('');
      setBothReason('');
      refresh();
    } catch {
      notify('Could not save the threshold', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ mb: 2 }} data-testid={`alert-card-${type}`}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {ALERT_TYPE_LABEL[type]}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fires above a per-market amount. The threshold is region-keyed (the
          currencies differ), so a change records a reason.
        </Typography>
        <Stack spacing={2}>
          {sorted.map((row) => (
            <MonetaryRow key={row.id} row={row} email={email} />
          ))}
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Apply one threshold to both markets
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <TextField
              label="Threshold"
              value={bothValue}
              onChange={(e) => setBothValue(e.target.value)}
              type="number"
              margin="dense"
              size="small"
              sx={{ minWidth: 140 }}
              inputProps={{ 'data-testid': `alert-both-input-${type}` }}
            />
            <TextField
              label="Reason"
              value={bothReason}
              onChange={(e) => setBothReason(e.target.value)}
              margin="dense"
              size="small"
              sx={{ minWidth: 200 }}
              inputProps={{ 'data-testid': `alert-both-reason-${type}` }}
            />
            <Button
              variant="contained"
              disabled={!bothValue.trim() || !bothReason.trim() || busy}
              onClick={() => setPendingCross(true)}
              data-testid={`alert-both-save-${type}`}
            >
              Apply to both markets
            </Button>
          </Stack>
        </Box>

        <CrossRegionConfirm
          isOpen={pendingCross}
          loading={busy}
          regions={sorted.map((r) => marketToRegion(r.market as Market))}
          what={`${ALERT_TYPE_LABEL[type]} threshold`}
          onConfirm={() => void saveBoth()}
          onClose={() => setPendingCross(false)}
        />
      </CardContent>
    </Card>
  );
};

/** The suspicious_activity card: a global count in a window, forced-reason edit. */
const CountTypeCard = ({ row, email }: { row: AlertConfig; email: string }) => {
  const [count, setCount] = useState(String(row.thresholdCount ?? ''));
  const [window, setWindow] = useState(String(row.windowMinutes ?? ''));
  return (
    <Card sx={{ mb: 2 }} data-testid={`alert-card-${row.alertType}`}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {ALERT_TYPE_LABEL[row.alertType]}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fires on a count of events in a window. Global (not
          currency-dependent).
        </Typography>
        <RowToggles row={row} email={email} />
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 1 }}
        >
          <TextField
            label="Event count"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            type="number"
            margin="dense"
            size="small"
            sx={{ minWidth: 140 }}
            inputProps={{ 'data-testid': `alert-count-${row.id}` }}
          />
          <TextField
            label="Window (minutes)"
            value={window}
            onChange={(e) => setWindow(e.target.value)}
            type="number"
            margin="dense"
            size="small"
            sx={{ minWidth: 160 }}
            inputProps={{ 'data-testid': `alert-window-${row.id}` }}
          />
          <JustifiedAction
            resource="alertConfigs"
            capability="alerts:write"
            id={row.id}
            label="Save threshold"
            dialogTitle="Change the suspicious-activity threshold"
            dialogIntro="This threshold drives when the alert fires. Record why it is changing."
            buildData={(reason) => ({
              thresholdCount: Number(count),
              windowMinutes: Number(window),
              lastChangeReason: reason,
              updatedBy: email,
              updatedAt: nowIso(),
            })}
            previousData={row}
            successMessage="Threshold saved"
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

/** The system_error card: global, no threshold, only the toggles. */
const SystemErrorCard = ({
  row,
  email,
}: {
  row: AlertConfig;
  email: string;
}) => (
  <Card sx={{ mb: 2 }} data-testid={`alert-card-${row.alertType}`}>
    <CardContent>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {ALERT_TYPE_LABEL[row.alertType]}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fires on any system error. No threshold. Global.
      </Typography>
      <RowToggles row={row} email={email} />
    </CardContent>
  </Card>
);

/** The global channel config: the write-only Slack webhook plus the email routing. */
const ChannelConfigCard = ({
  row,
  email,
}: {
  row: AlertChannelConfig;
  email: string;
}) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();

  const [newWebhook, setNewWebhook] = useState('');
  const [webhookReason, setWebhookReason] = useState('');
  const [recipients, setRecipients] = useState(row.emailRecipients.join(', '));
  const [frequency, setFrequency] = useState(row.emailFrequency);

  const webhookSet = row.slackWebhookUrl != null && row.slackWebhookUrl !== '';

  const write = async (data: Record<string, unknown>, ok: string) => {
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'alerts:write',
        resource: 'alertChannelConfigs',
        id: row.id,
        data: { ...data, updatedBy: email, updatedAt: nowIso() },
        previousData: row,
      });
      notify(ok, { type: 'success' });
      refresh();
    } catch {
      notify('Could not save the channel config', { type: 'error' });
    }
  };

  // The webhook write: write-only (only writes when a new value is entered, a blank input
  // never clears it) and forced-reason. The secret is never read back; the audit records
  // [redacted] (slackWebhookUrl is in SENSITIVE_AUDIT_FIELDS).
  const saveWebhook = async () => {
    if (!newWebhook.trim() || !webhookReason.trim()) return;
    await write(
      {
        slackWebhookUrl: newWebhook.trim(),
        lastChangeReason: webhookReason.trim(),
      },
      'Webhook updated',
    );
    setNewWebhook('');
    setWebhookReason('');
  };

  const saveEmail = async () => {
    const list = recipients
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
    await write(
      { emailRecipients: list, emailFrequency: frequency },
      'Email routing saved',
    );
  };

  return (
    <Card sx={{ mb: 2 }} data-testid="alert-channel-config">
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Channels
        </Typography>

        {/* Slack webhook (SECRET: write-only, redacted in the audit, the S2 treatment). */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Typography variant="body2">Slack webhook</Typography>
          <Chip
            size="small"
            label={webhookSet ? 'Webhook is set' : 'No webhook set'}
            color={webhookSet ? 'success' : 'default'}
            variant={webhookSet ? 'filled' : 'outlined'}
            data-testid="alert-webhook-tell"
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
          The Slack incoming-webhook URL. It is a secret and is never shown;
          enter a new value to change it. A change records a reason.
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <TextField
            label="Enter a new webhook to change"
            value={newWebhook}
            onChange={(e) => setNewWebhook(e.target.value)}
            type="password"
            margin="dense"
            size="small"
            sx={{ minWidth: 240 }}
            inputProps={{ 'data-testid': 'alert-webhook-input' }}
          />
          <TextField
            label="Reason"
            value={webhookReason}
            onChange={(e) => setWebhookReason(e.target.value)}
            margin="dense"
            size="small"
            sx={{ minWidth: 200 }}
            inputProps={{ 'data-testid': 'alert-webhook-reason' }}
          />
          <Button
            variant="contained"
            disabled={!newWebhook.trim() || !webhookReason.trim()}
            onClick={() => void saveWebhook()}
            data-testid="alert-save-webhook"
          >
            Save webhook
          </Button>
        </Stack>

        {/* Email digest routing (NOT secret, plain audited). */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Email digest
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <TextField
              label="Recipients (comma separated)"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              margin="dense"
              size="small"
              sx={{ minWidth: 280 }}
              inputProps={{ 'data-testid': 'alert-recipients' }}
            />
            <TextField
              select
              label="Frequency"
              value={frequency}
              onChange={(e) =>
                setFrequency(e.target.value as 'daily' | 'weekly')
              }
              margin="dense"
              size="small"
              sx={{ minWidth: 140 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'alert-frequency' }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </TextField>
            <Button
              variant="contained"
              onClick={() => void saveEmail()}
              data-testid="alert-save-email"
            >
              Save email routing
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

const AlertConfigInner = ({
  configs,
  channel,
}: {
  configs: AlertConfig[];
  channel: AlertChannelConfig | undefined;
}) => {
  const { identity } = useGetIdentity();
  const email = String(identity?.email ?? identity?.id ?? 'unknown');

  const byType = (type: AlertType) =>
    configs.filter((c) => c.alertType === type);

  return (
    <Box sx={{ maxWidth: 900 }} data-testid="alert-config-page">
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Internal alerts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Operational alert configuration. The backend fires and delivers these
        alerts; this configures the thresholds and channels it reads. Threshold
        changes record a reason.
      </Typography>

      {ALERT_TYPES.map((type) => {
        const rows = byType(type);
        if (rows.length === 0) return null;
        if (isMonetaryAlert(type)) {
          return (
            <MonetaryTypeCard
              key={type}
              type={type}
              rows={rows}
              email={email}
            />
          );
        }
        if (type === 'suspicious_activity') {
          return <CountTypeCard key={type} row={rows[0]} email={email} />;
        }
        return <SystemErrorCard key={type} row={rows[0]} email={email} />;
      })}

      {channel && <ChannelConfigCard row={channel} email={email} />}
    </Box>
  );
};

const ALL = {
  pagination: { page: 1, perPage: 100 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

/** The surface without the access wrapper. Exported for testing. */
export const AlertConfigView = () => {
  const configs = useGetList<AlertConfig>('alertConfigs', ALL);
  const channels = useGetList<AlertChannelConfig>('alertChannelConfigs', ALL);
  if (configs.isLoading || channels.isLoading) return <Loading />;
  return (
    <AlertConfigInner
      configs={configs.data ?? []}
      channel={(channels.data ?? [])[0]}
    />
  );
};

/**
 * Gated entry point mounted at /alert-config. super_admin-only (alerts:write): a mis-set
 * threshold is consequential operational config, so this sits at the tightest tier. The
 * fired-alert trail is read-gated more broadly (AlertList).
 */
export const AlertConfigPage = () => (
  <RequireAccess capability="alerts:write">
    <AlertConfigView />
  </RequireAccess>
);
